import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { MapViewEntity, MapViewNode } from "@/lib/core/schemas/map-view";

export type EntityPanelProps = {
  node: MapViewNode | null;
  entity: MapViewEntity | null;
  side: "bottom" | "right";
  rawState?: unknown;
  onClose: () => void;
  onViewSpatialGrid?: (zoneId: string) => void;
  /** Resolved display name for entity.locatedIn, derived by the caller
   * (maps-tab.tsx) from the already-loaded MapView.entities — never
   * fetched here. Falls back to the raw id when not provided (e.g. the
   * container isn't present in the caller's entity list). */
  locatedInName?: string | null;
};

const componentChipKeys = [
  "gridPresence",
  "spatialPosition",
  "blocksMovement",
  "blocksSight",
] as const;

// node and entity are mutually exclusive at the caller; node wins if both set
export const EntityPanel = ({
  node,
  entity,
  side,
  rawState,
  onClose,
  onViewSpatialGrid,
  locatedInName,
}: EntityPanelProps) => {
  const [showRaw, setShowRaw] = useState(false);
  const open = node !== null || entity !== null;

  return (
    <Sheet
      onOpenChange={(o) => {
        if (!o) {
          setShowRaw(false);
          onClose();
        }
      }}
      open={open}
    >
      <SheetContent side={side}>
        {node !== null && (
          <>
            <SheetHeader>
              <SheetTitle>{node.label}</SheetTitle>
              <p className="text-xs text-zinc-500">{node.modeProfile}</p>
            </SheetHeader>
            <div className="mt-4">
              <p className="mb-1 text-[10px] text-zinc-500 uppercase tracking-wide">
                Entity id
              </p>
              <p className="mb-4 font-mono text-xs text-zinc-300">{node.id}</p>
              <p className="mb-2 text-[10px] text-zinc-500 uppercase tracking-wide">
                Contained entities ({node.containedEntities.length})
              </p>
              <ul className="space-y-1">
                {node.containedEntities.length === 0 && (
                  <li className="text-xs text-zinc-500 italic">none</li>
                )}
                {node.containedEntities.map((e) => (
                  <li
                    className="flex items-center justify-between rounded bg-zinc-800 px-3 py-2 text-xs text-zinc-200"
                    key={e.id}
                  >
                    <span>{e.name}</span>
                    <span className="text-[9px] text-zinc-500">
                      {e.archetype}
                    </span>
                  </li>
                ))}
              </ul>
              {onViewSpatialGrid !== undefined && (
                <button
                  className="mt-4 w-full rounded border border-zinc-700 bg-zinc-800 px-3 py-2 text-xs text-zinc-200 hover:bg-zinc-700"
                  onClick={() => onViewSpatialGrid(node.id)}
                  type="button"
                >
                  View spatial grid →
                </button>
              )}
            </div>
          </>
        )}

        {node === null && entity !== null && (
          <>
            <SheetHeader>
              <SheetTitle>{entity.name}</SheetTitle>
              <p className="text-xs text-zinc-500">{entity.scenarioSide}</p>
            </SheetHeader>
            <div className="mt-4">
              <p className="mb-1 text-[10px] text-zinc-500 uppercase tracking-wide">
                Entity id
              </p>
              <p className="mb-4 font-mono text-xs text-zinc-300">
                {entity.id}
              </p>
              <p className="mb-1 text-[10px] text-zinc-500 uppercase tracking-wide">
                Located in
              </p>
              <p className="mb-4 font-mono text-xs text-zinc-300">
                {entity.locatedIn === null
                  ? "(none)"
                  : (locatedInName ?? entity.locatedIn)}
              </p>
              {entity.position !== undefined && (
                <>
                  <p className="mb-1 text-[10px] text-zinc-500 uppercase tracking-wide">
                    Position
                  </p>
                  <p className="mb-4 font-mono text-xs text-zinc-300">
                    ({entity.position.x}, {entity.position.y},{" "}
                    {entity.position.z})
                  </p>
                </>
              )}
              <p className="mb-2 text-[10px] text-zinc-500 uppercase tracking-wide">
                Components
              </p>
              <div className="flex flex-wrap gap-1">
                {componentChipKeys.filter(
                  (key) => entity.components[key] === true
                ).length === 0 && (
                  <span className="text-xs text-zinc-500 italic">none</span>
                )}
                {componentChipKeys
                  .filter((key) => entity.components[key] === true)
                  .map((key) => (
                    <span
                      className="rounded bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-300"
                      key={key}
                    >
                      {key}
                    </span>
                  ))}
              </div>
            </div>
          </>
        )}

        {(node !== null || entity !== null) && rawState !== undefined && (
          <div className="mt-4">
            <button
              className="w-full rounded border border-zinc-700 px-3 py-1 text-xs text-zinc-400"
              onClick={() => setShowRaw((v) => !v)}
              type="button"
            >
              {showRaw ? "Hide raw state" : "Show raw state ▾"}
            </button>
            {showRaw && (
              <pre className="mt-2 overflow-auto rounded bg-black p-2 text-[10px] text-zinc-400">
                {JSON.stringify(rawState, null, 2)}
              </pre>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
